import { Trans, useTranslation } from 'react-i18next';

import type { Menu } from '@actual-app/components/menu';
import { Select } from '@actual-app/components/select';
import { Text } from '@actual-app/components/text';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import type { AiCategorizeSuggestion } from '@actual-app/core/server/ai/app';

import { FinancialText } from '#components/FinancialText';

export type SuggestionGroup = {
  key: string;
  payeeId: string | null;
  label: string;
  categoryId: string | null;
  reason: string;
  transactions: AiCategorizeSuggestion[];
  include: boolean;
  createRule: boolean;
  canCreateRule: boolean;
};

type AiCategorizeSuggestionRowProps = {
  group: SuggestionGroup;
  categoryOptions: Array<
    readonly [string, string] | readonly [typeof Menu.label, string]
  >;
  formatAmount: (amount: number) => string;
  onChange: (fields: Partial<SuggestionGroup>) => void;
};

export function AiCategorizeSuggestionRow({
  group,
  categoryOptions,
  formatAmount,
  onChange,
}: AiCategorizeSuggestionRowProps) {
  const { t } = useTranslation();
  const total = group.transactions.reduce((sum, tx) => sum + tx.amount, 0);

  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        padding: '8px 0',
        borderBottom: `1px solid ${theme.tableBorder}`,
        opacity: group.include ? 1 : 0.5,
      }}
    >
      <input
        type="checkbox"
        aria-label={t('Include {{payee}}', { payee: group.label })}
        checked={group.include}
        onChange={e => onChange({ include: e.target.checked })}
      />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ fontWeight: 600 }}>{group.label}</Text>
        <Text style={{ fontSize: 12, color: theme.pageTextLight }}>
          {group.reason}
        </Text>
      </View>
      <View style={{ width: 110, textAlign: 'right' }}>
        <FinancialText>{formatAmount(total)}</FinancialText>
        <Text style={{ fontSize: 12, color: theme.pageTextLight }}>
          <Trans count={group.transactions.length}>
            {{ count: group.transactions.length }} transactions
          </Trans>
        </Text>
      </View>
      <View style={{ width: 200 }}>
        <Select
          options={categoryOptions}
          value={group.categoryId ?? ''}
          defaultLabel={t('Choose a category')}
          onChange={categoryId => {
            if (typeof categoryId === 'string') {
              onChange({ categoryId, include: true });
            }
          }}
        />
      </View>
      <label
        style={{
          width: 90,
          fontSize: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          visibility: group.canCreateRule ? 'visible' : 'hidden',
        }}
      >
        <input
          type="checkbox"
          checked={group.createRule}
          onChange={e => onChange({ createRule: e.target.checked })}
        />
        <Trans>Make rule</Trans>
      </label>
    </View>
  );
}
